import { useEffect, useState } from 'react';
import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import {
  CalendarRange,
  KeyRound,
  Plus,
  RotateCcw,
  Search,
  ShieldCheck,
  Star,
  Trash2,
  Users,
  X,
} from 'lucide-react';
import { api } from '../../api/client';
import { useAuthStore } from '../../store/auth';
import EmptyState from '../../components/EmptyState';
import ErrorBanner from '../../components/ErrorBanner';
import PaginationFooter from '../../components/PaginationFooter';
import { SkeletonTable } from '../../components/Skeleton';
import { toast } from '../../lib/toast';
import { formatDate } from '../../lib/format';

const ENTITLEMENT_KEYS = ['starter', 'booster', 'pro', 'autoPilot', 'agency', 'enterprise'];
// Display labels for entitlement keys. The functional key stays `enterprise`
// everywhere, but it's branded as the "Reseller" offer — so the admin sees
// "Reseller", matching the customer-facing billing page.
const ENTITLEMENT_LABEL = {
  starter: 'Starter',
  booster: 'Booster',
  pro: 'Pro',
  autoPilot: 'AutoPilot',
  agency: 'Agency',
  enterprise: 'Reseller',
};
const ENTITLEMENT_COLOR = {
  starter: 'bg-emerald-50 text-emerald-700',
  booster: 'bg-amber-50 text-amber-700',
  pro: 'bg-brand-50 text-brand-700',
  autoPilot: 'bg-cyan-50 text-cyan-700',
  agency: 'bg-violet-50 text-violet-700',
  enterprise: 'bg-rose-50 text-rose-700',
};

// Where an account came from (User.source). Legacy users created before the
// field existed read back as null/undefined and render as "Unknown".
const SOURCE_META = {
  free_signup: { label: 'Free Signup', color: 'bg-emerald-50 text-emerald-700' },
  launchpad: { label: 'Launchpad JV', color: 'bg-brand-50 text-brand-700' },
  admin: { label: 'Admin', color: 'bg-violet-50 text-violet-700' },
  reseller: { label: 'Reseller', color: 'bg-amber-50 text-amber-700' },
};

function SourceBadge({ source }) {
  const meta = SOURCE_META[source];
  if (!meta) {
    return <span className="text-xs text-ink-400">Unknown</span>;
  }
  return (
    <span className={`text-[11px] font-medium px-2 py-0.5 rounded-full whitespace-nowrap ${meta.color}`}>
      {meta.label}
    </span>
  );
}

function ownedKeys(ents) {
  if (!ents) return [];
  const owned = [];
  for (const k of ['starter', 'booster', 'pro', 'agency', 'enterprise']) {
    if (ents[k]) owned.push(k);
  }
  if (ents.autoPilot?.active) {
    const exp = ents.autoPilot.expiresAt ? new Date(ents.autoPilot.expiresAt) : null;
    if (!exp || exp.getTime() > Date.now()) owned.push('autoPilot');
  }
  return owned;
}

// Admin → Users page. Combines the "Create user" form with the searchable
// users table and reset-password modal — these three pieces are tightly
// linked (create / list / mutate a user), so they live on one page.
export default function UsersPage() {
  const { user } = useAuthStore();
  const qc = useQueryClient();
  // `searchInput` is the immediate value bound to the input; `query` is the
  // debounced value we actually send to the API. This keeps the table from
  // re-fetching on every keystroke.
  const [searchInput, setSearchInput] = useState('');
  const [query, setQuery] = useState('');
  const [createdFrom, setCreatedFrom] = useState('');
  const [createdTo, setCreatedTo] = useState('');
  const [resellerFilter, setResellerFilter] = useState(''); // '' | 'none' | <id>
  const [showDeleted, setShowDeleted] = useState(false);
  const [page, setPage] = useState(1);
  const PAGE_SIZE = 25;
  const [createForm, setCreateForm] = useState({
    email: '',
    password: '',
    name: '',
    role: 'user',
  });
  const [resetTarget, setResetTarget] = useState(null);
  const [resetPassword, setResetPassword] = useState('');
  const [createError, setCreateError] = useState('');

  // Debounce the search input ~300ms before it triggers an API call.
  useEffect(() => {
    const id = setTimeout(() => setQuery(searchInput), 300);
    return () => clearTimeout(id);
  }, [searchInput]);

  // Whenever any filter changes, jump back to page 1 — otherwise a narrow
  // filter on page 5 could land the admin on an empty page.
  useEffect(() => {
    setPage(1);
  }, [query, createdFrom, createdTo, resellerFilter, showDeleted]);

  // Build the query string only with the params the admin has actually set.
  const filterParams = new URLSearchParams();
  if (query) filterParams.set('q', query);
  if (createdFrom) filterParams.set('createdFrom', createdFrom);
  if (createdTo) filterParams.set('createdTo', createdTo);
  if (resellerFilter) filterParams.set('parentResellerId', resellerFilter);
  if (showDeleted) filterParams.set('includeDeleted', 'true');
  filterParams.set('page', String(page));
  filterParams.set('limit', String(PAGE_SIZE));
  const filterQs = filterParams.toString();

  const usersQuery = useQuery({
    queryKey: ['admin-users', query, createdFrom, createdTo, resellerFilter, showDeleted, page, PAGE_SIZE],
    queryFn: () =>
      api.get(`/api/admin/users?${filterQs}`).then((r) => r.data),
    // Keep showing the previous page while the next page is loading so the
    // table doesn't flicker into an empty skeleton on every paginate.
    placeholderData: keepPreviousData,
  });

  const users = usersQuery.data?.users || [];
  const total = usersQuery.data?.total ?? 0;
  const totalPages = usersQuery.data?.totalPages ?? 1;
  const scope = usersQuery.data?.scope;
  const isReseller = scope?.kind === 'reseller';
  // Load reseller list for the assignment dropdown (admin-only).
  const resellersQuery = useQuery({
    queryKey: ['admin-resellers-pick'],
    queryFn: () => api.get('/api/admin/resellers').then((r) => r.data.resellers),
    enabled: scope?.kind === 'admin',
    retry: false,
  });

  const hasActiveFilter = Boolean(query || createdFrom || createdTo || resellerFilter);
  const clearFilters = () => {
    setSearchInput('');
    setQuery('');
    setCreatedFrom('');
    setCreatedTo('');
    setResellerFilter('');
  };

  const createUser = useMutation({
    mutationFn: (body) => api.post('/api/admin/users', body).then((r) => r.data.user),
    onSuccess: (created) => {
      qc.invalidateQueries({ queryKey: ['admin-users'] });
      qc.invalidateQueries({ queryKey: ['admin-stats'] });
      setCreateForm({ email: '', password: '', name: '', role: 'user' });
      setCreateError('');
      toast.success(`Created ${created.email}`);
    },
    onError: (err) => {
      setCreateError(err.response?.data?.error || 'Could not create user');
      toast.error(err, 'Could not create user');
    },
  });

  const updateUser = useMutation({
    mutationFn: ({ id, body }) =>
      api.put(`/api/admin/users/${id}`, body).then((r) => r.data.user),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin-users'] });
      toast.success('User updated');
    },
    onError: (err) => toast.error(err, 'Could not update user'),
  });

  const resetPasswordMutation = useMutation({
    mutationFn: ({ id, password }) =>
      api.post(`/api/admin/users/${id}/reset-password`, { password }),
    onSuccess: () => {
      setResetTarget(null);
      setResetPassword('');
      toast.success('Password reset');
    },
    onError: (err) => toast.error(err, 'Could not reset password'),
  });

  const deleteUser = useMutation({
    mutationFn: (id) => api.delete(`/api/admin/users/${id}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin-users'] });
      qc.invalidateQueries({ queryKey: ['admin-stats'] });
      toast.success('User deleted');
    },
    onError: (err) => toast.error(err, 'Could not delete user'),
  });

  const restoreUser = useMutation({
    mutationFn: (id) => api.post(`/api/admin/users/${id}/restore`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin-users'] });
      qc.invalidateQueries({ queryKey: ['admin-stats'] });
      toast.success('User restored');
    },
    onError: (err) => toast.error(err, 'Could not restore user'),
  });

  const grantEntitlement = useMutation({
    mutationFn: ({ id, body }) =>
      api.post(`/api/admin/users/${id}/entitlements`, body).then((r) => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin-users'] });
      toast.success('Entitlement granted');
    },
    onError: (err) => toast.error(err, 'Could not grant entitlement'),
  });

  const revokeEntitlement = useMutation({
    mutationFn: ({ id, key }) =>
      api.delete(`/api/admin/users/${id}/entitlements/${key}`).then((r) => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin-users'] });
      toast.success('Entitlement revoked');
    },
    onError: (err) => toast.error(err, 'Could not revoke entitlement'),
  });

  return (
    <div className="space-y-6">
      {/* Create user */}
      <div className="card">
        <div className="section-header">
          <div className="section-title">
            <span className="section-icon">
              <Plus className="w-4 h-4" />
            </span>
            Create user
          </div>
        </div>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            createUser.mutate(createForm);
          }}
          className="grid grid-cols-1 md:grid-cols-5 gap-2"
        >
          <input
            className="input md:col-span-2"
            placeholder="Email"
            type="email"
            required
            value={createForm.email}
            onChange={(e) => setCreateForm({ ...createForm, email: e.target.value })}
          />
          <input
            className="input"
            placeholder="Password (min 8)"
            type="password"
            minLength={8}
            required
            value={createForm.password}
            onChange={(e) => setCreateForm({ ...createForm, password: e.target.value })}
          />
          <input
            className="input"
            placeholder="Name (optional)"
            value={createForm.name}
            onChange={(e) => setCreateForm({ ...createForm, name: e.target.value })}
          />
          <select
            className="input"
            value={createForm.role}
            onChange={(e) => setCreateForm({ ...createForm, role: e.target.value })}
          >
            <option value="user">user</option>
            <option value="admin">admin</option>
          </select>
          <button
            className="btn-primary md:col-span-5"
            disabled={createUser.isPending}
          >
            <Plus className="w-4 h-4" />
            {createUser.isPending ? 'Creating…' : 'Create user'}
          </button>
        </form>
        {createError && (
          <div className="mt-3">
            <ErrorBanner body={createError} onDismiss={() => setCreateError('')} />
          </div>
        )}
      </div>

      {/* Reseller-scope banner (visible only when caller is a reseller) */}
      {isReseller && (
        <div className="rounded-xl border border-brand-100 bg-brand-50/60 text-brand-800 text-sm p-4 flex items-start gap-3">
          <Users className="w-5 h-5 flex-none mt-0.5" />
          <div>
            <div className="font-semibold">
              Showing your customers ({scope.brandName})
            </div>
            <div className="mt-1 text-xs">
              Resellers see only users attached to their account via parentResellerId.
              New customers are linked automatically when they purchase through your
              Launchpad webhook URL.
            </div>
          </div>
        </div>
      )}

      {/* Users table */}
      <div className="card">
        <div className="section-header flex-wrap gap-3">
          <div className="section-title">
            <span className="section-icon">
              <Users className="w-4 h-4" />
            </span>
            Users
            {usersQuery.data && (
              <span className="text-xs text-ink-400 font-normal ml-1">({total})</span>
            )}
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-ink-400 pointer-events-none" />
              <input
                className="input pl-10 max-w-xs"
                placeholder="Search by email or name…"
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
              />
            </div>
            <div
              className="flex items-center gap-1.5 text-xs text-ink-500"
              title="Filter by signup date"
            >
              <CalendarRange className="w-4 h-4 text-ink-400" />
              <input
                type="date"
                className="input !py-1.5 text-xs"
                value={createdFrom}
                max={createdTo || undefined}
                onChange={(e) => setCreatedFrom(e.target.value)}
                aria-label="Signed up from"
              />
              <span className="text-ink-400">→</span>
              <input
                type="date"
                className="input !py-1.5 text-xs"
                value={createdTo}
                min={createdFrom || undefined}
                onChange={(e) => setCreatedTo(e.target.value)}
                aria-label="Signed up to"
              />
            </div>
            {scope?.kind === 'admin' && (
              <select
                className="input !py-1.5 text-xs"
                value={resellerFilter}
                onChange={(e) => setResellerFilter(e.target.value)}
                aria-label="Filter by reseller"
                title="Filter by reseller"
              >
                <option value="">All resellers</option>
                <option value="none">Direct (no reseller)</option>
                {(resellersQuery.data || []).map((r) => (
                  <option key={r._id} value={r._id}>
                    {r.brandName}
                  </option>
                ))}
              </select>
            )}
            <label
              className="flex items-center gap-1.5 text-xs text-ink-600 cursor-pointer select-none whitespace-nowrap"
              title="Show soft-deleted (recoverable) users"
            >
              <input
                type="checkbox"
                checked={showDeleted}
                onChange={(e) => setShowDeleted(e.target.checked)}
              />
              Show deleted
            </label>
            {hasActiveFilter && (
              <button
                type="button"
                className="btn-ghost text-xs"
                onClick={clearFilters}
                title="Clear all filters"
              >
                <X className="w-3 h-3" /> Clear
              </button>
            )}
          </div>
        </div>
        {usersQuery.isLoading ? (
          <SkeletonTable rows={6} cols={6} wrap={false} />
        ) : !users.length ? (
          <EmptyState
            icon={Users}
            title={hasActiveFilter ? 'No users match these filters.' : 'No users yet.'}
            body={hasActiveFilter ? 'Try clearing filters or widening the date range.' : undefined}
          />
        ) : (
          <div className="overflow-x-auto scroll-soft">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Email</th>
                  <th>Name</th>
                  <th>Source</th>
                  <th>Role</th>
                  <th>Entitlements</th>
                  {!isReseller && <th>Reseller</th>}
                  <th className="text-right">Domains</th>
                  <th>Created</th>
                  <th className="text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {users.map((u) => (
                  <tr key={u._id}>
                    <td className="text-ink-800">
                      <div className="flex items-center gap-1.5">
                        {u.email}
                        {u.prioritySupport && (
                          <Star
                            className="w-3.5 h-3.5 text-amber-500 fill-amber-400"
                            title="Priority support"
                          />
                        )}
                        {u.isDeleted && (
                          <span className="text-[10px] font-medium px-1.5 py-0.5 rounded bg-rose-50 text-rose-600">
                            Deleted
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="text-ink-700">{u.name || '—'}</td>
                    <td>
                      <SourceBadge source={u.source} />
                    </td>
                    <td>
                      <select
                        className="text-xs border border-gray-200 rounded-lg px-2 py-1 bg-white"
                        value={u.role}
                        onChange={(e) =>
                          updateUser.mutate({
                            id: u._id,
                            body: { role: e.target.value },
                          })
                        }
                        disabled={u._id === user._id}
                      >
                        <option value="user">user</option>
                        <option value="admin">admin</option>
                      </select>
                    </td>
                    <td>
                      <EntitlementCell
                        user={u}
                        onGrant={(body) => grantEntitlement.mutate({ id: u._id, body })}
                        onRevoke={(key) => revokeEntitlement.mutate({ id: u._id, key })}
                        grantPending={grantEntitlement.isPending}
                        revokePending={revokeEntitlement.isPending}
                      />
                    </td>
                    {!isReseller && (
                      <td>
                        <select
                          className="text-xs border border-gray-200 rounded-lg px-2 py-1 bg-white max-w-[140px]"
                          value={u.parentResellerId || ''}
                          onChange={(e) =>
                            updateUser.mutate({
                              id: u._id,
                              body: { parentResellerId: e.target.value || null },
                            })
                          }
                        >
                          <option value="">— none —</option>
                          {(resellersQuery.data || []).map((r) => (
                            <option key={r._id} value={r._id}>
                              {r.brandName}
                            </option>
                          ))}
                        </select>
                      </td>
                    )}
                    <td className="text-right">{u.domainCount}</td>
                    <td className="text-ink-400 whitespace-nowrap">
                      {formatDate(u.createdAt)}
                    </td>
                    <td className="text-right whitespace-nowrap">
                      <button
                        className={`btn-icon ${u.prioritySupport ? '!text-amber-500 !bg-amber-50' : ''}`}
                        title={u.prioritySupport ? 'Priority support — click to remove' : 'Mark as priority support'}
                        onClick={() =>
                          updateUser.mutate({
                            id: u._id,
                            body: { prioritySupport: !u.prioritySupport },
                          })
                        }
                      >
                        <Star
                          className={`w-4 h-4 ${u.prioritySupport ? 'fill-amber-400' : ''}`}
                        />
                      </button>
                      <button
                        className="btn-icon"
                        title="Reset password"
                        onClick={() => {
                          setResetTarget(u);
                          setResetPassword('');
                        }}
                      >
                        <KeyRound className="w-4 h-4" />
                      </button>
                      {u.isDeleted ? (
                        <button
                          className="btn-icon hover:!text-emerald-600 hover:!bg-emerald-50"
                          title="Restore user"
                          onClick={() => restoreUser.mutate(u._id)}
                        >
                          <RotateCcw className="w-4 h-4" />
                        </button>
                      ) : (
                        <button
                          className="btn-icon hover:!text-rose-600 hover:!bg-rose-50 disabled:opacity-30 disabled:hover:bg-transparent disabled:hover:text-ink-400"
                          title="Delete user"
                          disabled={u._id === user._id}
                          onClick={() => {
                            if (
                              window.confirm(
                                `Delete ${u.email}? Their account is deactivated and hidden, but all their data is kept and can be restored later.`,
                              )
                            ) {
                              deleteUser.mutate(u._id);
                            }
                          }}
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        <PaginationFooter
          page={page}
          totalPages={totalPages}
          total={total}
          pageSize={PAGE_SIZE}
          onPageChange={setPage}
          isFetching={usersQuery.isFetching}
        />
      </div>

      {/* Reset password modal */}
      {resetTarget && (
        <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center px-4 z-30">
          <div className="card w-full max-w-md">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-base font-semibold flex items-center gap-2">
                <KeyRound className="w-4 h-4 text-brand-600" />
                Reset password
              </h3>
              <button
                className="btn-icon"
                onClick={() => setResetTarget(null)}
                title="Close"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
            <p className="text-sm text-ink-400 mb-3">
              Setting a new password for{' '}
              <span className="font-semibold text-ink-800">{resetTarget.email}</span>.
            </p>
            <div className="relative">
              <KeyRound className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-ink-400 pointer-events-none" />
              <input
                className="input pl-10"
                type="password"
                placeholder="New password (min 8 chars)"
                minLength={8}
                value={resetPassword}
                onChange={(e) => setResetPassword(e.target.value)}
              />
            </div>
            <div className="flex gap-2 mt-4 justify-end">
              <button className="btn-secondary" onClick={() => setResetTarget(null)}>
                Cancel
              </button>
              <button
                className="btn-primary"
                disabled={resetPassword.length < 8 || resetPasswordMutation.isPending}
                onClick={() =>
                  resetPasswordMutation.mutate({
                    id: resetTarget._id,
                    password: resetPassword,
                  })
                }
              >
                <ShieldCheck className="w-4 h-4" />
                {resetPasswordMutation.isPending ? 'Saving…' : 'Reset'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function EntitlementCell({ user, onGrant, onRevoke, grantPending, revokePending }) {
  const [adding, setAdding] = useState(false);
  const [pickAutopilot, setPickAutopilot] = useState(false);
  const owned = ownedKeys(user.entitlements);
  const available = ENTITLEMENT_KEYS.filter((k) => !owned.includes(k));

  function handleAdd(key) {
    if (key === 'autoPilot') {
      // AutoPilot needs an interval — show second step.
      setAdding(false);
      setPickAutopilot(true);
      return;
    }
    onGrant({ key });
    setAdding(false);
  }

  function handleAutopilotInterval(interval) {
    onGrant({ key: 'autoPilot', interval });
    setPickAutopilot(false);
  }

  function fmtAutopilotExpiry(ents) {
    const exp = ents?.autoPilot?.expiresAt;
    if (!exp) return '';
    const d = new Date(exp);
    return d.toISOString().slice(0, 10);
  }

  if (owned.length === 0 && !adding && !pickAutopilot) {
    return (
      <div className="flex items-center gap-1.5">
        <span className="text-xs text-ink-400">free</span>
        <button
          type="button"
          className="text-xs px-1.5 py-0.5 rounded border border-dashed border-gray-300 text-ink-500 hover:border-brand-400 hover:text-brand-700"
          onClick={() => setAdding(true)}
          disabled={grantPending}
          title="Grant an entitlement"
        >
          + add
        </button>
      </div>
    );
  }

  return (
    <div className="flex items-center gap-1 flex-wrap min-w-[180px]">
      {owned.map((key) => (
        <span
          key={key}
          className={`pill ${ENTITLEMENT_COLOR[key] || 'bg-gray-100 text-gray-700'} group/chip pr-1`}
          title={
            key === 'autoPilot'
              ? `expires ${fmtAutopilotExpiry(user.entitlements)}`
              : ''
          }
        >
          {ENTITLEMENT_LABEL[key] || key}
          {key === 'autoPilot' && (
            <span className="text-[10px] opacity-70 ml-1">
              {fmtAutopilotExpiry(user.entitlements)}
            </span>
          )}
          <button
            type="button"
            className="ml-1 opacity-50 group-hover/chip:opacity-100 hover:text-rose-600"
            onClick={() => {
              if (window.confirm(`Revoke "${ENTITLEMENT_LABEL[key] || key}" from ${user.email}?`)) {
                onRevoke(key);
              }
            }}
            disabled={revokePending}
            title="Revoke"
          >
            <X className="w-3 h-3" />
          </button>
        </span>
      ))}

      {pickAutopilot && (
        <span className="inline-flex items-center gap-1 pill bg-cyan-50 text-cyan-700">
          AutoPilot:
          <button
            type="button"
            className="text-xs underline hover:text-cyan-900"
            onClick={() => handleAutopilotInterval('month')}
            disabled={grantPending}
          >
            monthly
          </button>
          <span className="text-cyan-400">/</span>
          <button
            type="button"
            className="text-xs underline hover:text-cyan-900"
            onClick={() => handleAutopilotInterval('year')}
            disabled={grantPending}
          >
            yearly
          </button>
          <button
            type="button"
            className="ml-1 opacity-50 hover:opacity-100"
            onClick={() => setPickAutopilot(false)}
            title="Cancel"
          >
            <X className="w-3 h-3" />
          </button>
        </span>
      )}

      {adding && available.length > 0 && (
        <select
          autoFocus
          className="text-xs border border-gray-200 rounded px-1.5 py-0.5 bg-white"
          defaultValue=""
          disabled={grantPending}
          onChange={(e) => {
            if (e.target.value) handleAdd(e.target.value);
          }}
          onBlur={() => setAdding(false)}
        >
          <option value="">+ pick…</option>
          {available.map((k) => (
            <option key={k} value={k}>
              {ENTITLEMENT_LABEL[k] || k}
            </option>
          ))}
        </select>
      )}

      {!adding && !pickAutopilot && available.length > 0 && (
        <button
          type="button"
          className="text-xs px-1.5 py-0.5 rounded border border-dashed border-gray-300 text-ink-500 hover:border-brand-400 hover:text-brand-700"
          onClick={() => setAdding(true)}
          disabled={grantPending}
          title="Grant another entitlement"
        >
          +
        </button>
      )}
    </div>
  );
}
